import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { patientsService } from '../services/api/patients'
import type {
  CreatePatientData,
  PaginatedData,
  Patient,
  UpdatePatientData,
} from '../types'

export function usePatients(params?: {
  page?: number
  limit?: number
  search?: string
  sortBy?: string
  sortOrder?: 'ASC' | 'DESC'
}) {
  return useQuery({
    queryKey: ['patients', params],
    queryFn: async (): Promise<PaginatedData<Patient>> => {
      const result = await patientsService.list(params)
      if (!result.success) {
        throw new Error(
          result.message ?? 'Impossible de récupérer les patients'
        )
      }
      return (
        result.data ?? {
          data: [],
          pagination: {
            currentPage: 1,
            totalPages: 0,
            totalItems: 0,
            itemsPerPage: 10,
            hasNext: false,
            hasPrev: false,
          },
        }
      )
    },
    staleTime: 30 * 1000,
    refetchOnMount: 'always',
  })
}

export function usePatient(id: number) {
  return useQuery({
    queryKey: ['patients', id],
    queryFn: async () => {
      const result = await patientsService.getById(id)
      if (!result.success) {
        throw new Error(result.message ?? 'Patient introuvable')
      }
      return result.data
    },
    enabled: Number.isFinite(id) && id > 0,
  })
}

export function useCreatePatient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (patientData: CreatePatientData) => {
      const result = await patientsService.create(patientData)
      if (!result.success) {
        throw new Error(result.message ?? 'Création impossible')
      }
      return result.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] })
      toast.success('Patient créé avec succès')
    },
    onError: (error: Error) => {
      toast.error(`Erreur lors de la création: ${error.message}`)
    },
  })
}

export function useUpdatePatient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number
      data: UpdatePatientData
    }) => {
      const result = await patientsService.update(id, data)
      if (!result.success) {
        throw new Error(result.message ?? 'Mise à jour impossible')
      }
      return result.data
    },
    onSuccess: updatedPatient => {
      queryClient.invalidateQueries({ queryKey: ['patients'] })
      if (updatedPatient?.id) {
        queryClient.invalidateQueries({
          queryKey: ['patients', updatedPatient.id],
        })
      }
      toast.success('Patient mis à jour avec succès')
    },
    onError: (error: Error) => {
      toast.error(`Erreur lors de la mise à jour: ${error.message}`)
    },
  })
}

export function useDeletePatient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await patientsService.delete(id)
      if (!result.success) {
        throw new Error(result.message ?? 'Suppression impossible')
      }
      return id
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] })
      queryClient.invalidateQueries({ queryKey: ['appointments'] })
      toast.success('Patient supprimé avec succès')
    },
    onError: (error: Error) => {
      toast.error(`Erreur lors de la suppression: ${error.message}`)
    },
  })
}
